/**
 * Writing a response path from a click, and finding where a saved one lands.
 *
 * The platform's path syntax (see parseResponsePath on the API side) is dotted
 * keys, `[0]` for an array position and `[*]` for every item. A key that
 * contains `.`, `[` or `]` cannot be written in it at all, so such a node is
 * simply not addressable here either.
 */

export type PathSegment = string | number;

export type PathStep = { type: 'key'; name: string } | { type: 'index'; index: number } | { type: 'all' };

/** `data[0].b64_json` from `['data', 0, 'b64_json']`; with `anyItem`, `data[*].b64_json`. Null for the root, or a key the syntax cannot spell. */
export function buildResponsePath(segments: PathSegment[], anyItem = false): string | null {
  if (segments.length === 0) return null;
  let out = '';
  for (const segment of segments) {
    if (typeof segment === 'number') {
      out += anyItem ? '[*]' : `[${segment}]`;
    } else {
      if (segment === '' || /[.[\]]/.test(segment)) return null;
      out += out === '' ? segment : `.${segment}`;
    }
  }
  return out;
}

/** The steps of a saved path, or null when it is blank or malformed. */
export function parsePathSteps(path: string | null | undefined): PathStep[] | null {
  const source = (path ?? '').trim();
  if (!source) return null;
  const steps: PathStep[] = [];
  let i = 0;
  let afterDot = false;
  while (i < source.length) {
    const ch = source[i] as string;
    if (ch === '[') {
      if (afterDot) return null;
      const close = source.indexOf(']', i);
      if (close < 0) return null;
      const inner = source.slice(i + 1, close).trim();
      if (inner === '*') steps.push({ type: 'all' });
      else if (/^\d+$/.test(inner)) steps.push({ type: 'index', index: Number(inner) });
      else return null;
      i = close + 1;
      continue;
    }
    if (ch === '.') {
      if (i === 0 || afterDot) return null;
      afterDot = true;
      i += 1;
      continue;
    }
    if (ch === ']') return null;
    if (!afterDot && steps.length > 0) return null;
    let end = i;
    while (end < source.length && !'.[]'.includes(source[end] as string)) end += 1;
    steps.push({ type: 'key', name: source.slice(i, end) });
    afterDot = false;
    i = end;
  }
  return afterDot ? null : steps;
}

function stepMatches(step: PathStep, segment: PathSegment): boolean {
  if (step.type === 'key') return segment === step.name;
  if (typeof segment !== 'number') return false;
  return step.type === 'all' || step.index === segment;
}

/** Whether the node at `segments` is one the path points at (`[*]` matches any position). */
export function pathReaches(segments: PathSegment[], path: string | null | undefined): boolean {
  const steps = parsePathSteps(path);
  if (!steps || steps.length !== segments.length) return false;
  return steps.every((step, position) => stepMatches(step, segments[position] as PathSegment));
}

/** Whether the node lies on the way to what the path points at (an ancestor), so it can be kept open. */
export function pathPassesThrough(segments: PathSegment[], path: string | null | undefined): boolean {
  const steps = parsePathSteps(path);
  if (!steps || segments.length >= steps.length) return false;
  return segments.every((segment, position) => stepMatches(steps[position] as PathStep, segment));
}
